import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  Button,
  Alert,
  Container,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  Badge,
  Skeleton,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  LocalParking as ParkingIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { vehicleService, equipmentService, maintenanceService } from '../services/api';
import type { Vehicle, Equipment, EquipmentStats } from '../types';

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [lowStockItems, setLowStockItems] = useState<Equipment[]>([]);
  const [equipmentStats, setEquipmentStats] = useState<EquipmentStats | null>(null);
  const [maintenanceStats, setMaintenanceStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load vehicles, equipment, and maintenance stats
      const [vehiclesResponse, equipmentResponse, maintenanceStatsResponse] = await Promise.all([
        vehicleService.getAll(),
        equipmentService.getAll(),
        maintenanceService.getStats()
      ]);
      
      setVehicles(vehiclesResponse.data.data);
      setEquipment(equipmentResponse.data.data);
      setMaintenanceStats(maintenanceStatsResponse.data.data);
      
      // Calculate low stock items from main data (same approach as Equipment page)
      const lowStock = equipmentResponse.data.data.filter((item: Equipment) => 
        item.quantity_available <= item.minimum_stock_level
      );
      setLowStockItems(lowStock);
      
      // Calculate stats from main data (same approach as Equipment page)
      const allItems = equipmentResponse.data.data;
      const lowStockCount = allItems.filter((item: Equipment) => 
        item.quantity_available <= item.minimum_stock_level
      ).length;
      const inUseCount = allItems.filter((item: Equipment) => 
        item.quantity_total > item.quantity_available
      ).length;
      const totalValue = allItems.reduce((sum: number, item: Equipment) => 
        sum + (item.quantity_total * (item.unit_cost || 0)), 0
      );
      
      const calculatedStats: EquipmentStats = {
        total_items: allItems.length,
        total_categories: new Set(allItems.map(item => item.category)).size,
        low_stock_items: lowStockCount,
        out_of_stock_items: allItems.filter(item => item.quantity_available === 0).length,
        total_value: totalValue,
        items_in_use: inUseCount,
        total_usage_records: 0,
        categories: [],
      };
      setEquipmentStats(calculatedStats);
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
      setBootstrapped(true);
    }
  };

  const getStatusColor = (status: string, maintenanceStatus?: string) => {
    // Use vehicle operational status
    const effectiveStatus = status;
    
    switch (effectiveStatus) {
      case 'ready': return 'success';
      case 'under_maintenance': return 'warning';
      case 'washing': return 'info';
      case 'out': return 'primary';
      case 'returned': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string, maintenanceStatus?: string) => {
    // Use vehicle operational status
    const effectiveStatus = status;
    
    switch (effectiveStatus) {
      case 'ready': return 'Prêt';
      case 'under_maintenance': return 'En maintenance';
      case 'washing': return 'Lavage';
      case 'out': return 'Sorti';
      case 'returned': return 'Retourné';
      default: return effectiveStatus;
    }
  };

  const getMaintenanceAlerts = () => {
    // Get vehicles that have scheduled or overdue maintenance records
    return vehicles.filter(vehicle => {
      if (!vehicle.maintenance_records || vehicle.maintenance_records.length === 0) {
        return false;
      }
      // Check if vehicle has any scheduled or overdue maintenance
      return vehicle.maintenance_records.some(record => 
        record.status === 'scheduled' || record.status === 'overdue'
      );
    });
  };

  /** Vehicles that have at least one scheduled/overdue maintenance row (matches list + preview). */
  const getMaintenanceAlertVehicleCount = () => getMaintenanceAlerts().length;

  /** All scheduled maintenance rows for the tenant (often > distinct vehicles). Shown as context only. */
  const getScheduledMaintenanceRowCount = (): number | null => {
    if (!maintenanceStats || typeof maintenanceStats.scheduled !== 'number') {
      return null;
    }
    return maintenanceStats.scheduled;
  };

  const scheduledMaintenanceRows = getScheduledMaintenanceRowCount();

  const getAvailableVehicles = () => {
    return vehicles.filter(vehicle => vehicle.status === 'ready');
  };

  const getVehiclesInMaintenance = () => {
    return vehicles.filter(vehicle => vehicle.status === 'under_maintenance');
  };

  if (loading && !bootstrapped) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-busy="true"
        sx={{ minHeight: '70vh', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}
      >
        <Typography component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Chargement du tableau de bord…
        </Typography>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Skeleton variant="text" width={280} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={360} height={24} sx={{ mb: 4 }} />
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[0, 1, 2, 3].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
                <Card sx={{ height: 160, borderRadius: 2 }}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" height={48} sx={{ my: 1 }} />
                    <Skeleton variant="text" width="80%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card sx={{ borderRadius: 2, height: 320 }}>
                <CardContent>
                  <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Card sx={{ borderRadius: 2, height: 320 }}>
                <CardContent>
                  <Skeleton variant="text" width="55%" height={32} sx={{ mb: 2 }} />
                  {[0, 1, 2].map((j) => (
                    <Skeleton key={j} variant="rounded" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
      aria-busy={loading && bootstrapped}
    >
      {loading && bootstrapped && (
        <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 10 }} aria-label="Actualisation du tableau de bord" />
      )}
      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Bienvenue, {user?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vue d'ensemble de votre flotte et équipements
            </Typography>
          </Box>
          <Tooltip title="Actualiser les données">
            <span>
            <IconButton 
              onClick={loadData} 
              disabled={loading}
              aria-label="Actualiser les données"
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300' }
              }}
            >
              <RefreshIcon />
            </IconButton>
            </span>
          </Tooltip>
        </Stack>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => setError(null)}
            action={
              <Button color="inherit" size="small" onClick={loadData}>
                Réessayer
              </Button>
            }
          >
            {error}
          </Alert>
        )}
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card 
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Véhicules Disponibles
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {getAvailableVehicles().length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Prêts à l'utilisation
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <CarIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card 
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    En Maintenance
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {getVehiclesInMaintenance().length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    En cours de réparation
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <BuildIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card 
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(244, 67, 54, 0.3)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Alertes Maintenance
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {getMaintenanceAlertVehicleCount()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Véhicule(s) avec maintenance à traiter
                  </Typography>
                  {scheduledMaintenanceRows !== null && scheduledMaintenanceRows > 0 && (
                    <Typography variant="caption" sx={{ opacity: 0.85, mt: 0.5, display: 'block' }}>
                      {scheduledMaintenanceRows === 1
                        ? '1 intervention planifiée au total (tous véhicules)'
                        : `${scheduledMaintenanceRows} interventions planifiées au total (tous véhicules)`}
                    </Typography>
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <WarningIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card 
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Véhicules
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {vehicles.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Dans la flotte
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <TrendingUpIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Quick Actions */}
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Actions Rapides
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Accès direct aux fonctionnalités principales
            </Typography>
          </Stack>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<CarIcon />}
                onClick={() => navigate('/vehicles')}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Gérer les Véhicules
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<BuildIcon />}
                onClick={() => navigate('/maintenance')}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 152, 0, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Maintenance
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<ParkingIcon />}
                onClick={() => navigate('/parking')}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Parking
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<InventoryIcon />}
                onClick={() => navigate('/equipment')}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(156, 39, 176, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Équipements
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Vehicles and Alerts */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ 
            height: 400, 
            borderRadius: 2, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  Véhicules Récents
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/vehicles')}
                  sx={{ borderRadius: 2 }}
                >
                  Voir tout
                </Button>
              </Stack>
              
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {vehicles.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <CarIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Aucun véhicule trouvé
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Commencez par ajouter des véhicules à votre flotte
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                  {vehicles.slice(0, 5).map((vehicle, index) => (
                    <React.Fragment key={vehicle.id}>
                      <ListItem 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          mb: 1,
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            cursor: 'pointer'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                        onClick={() => navigate('/vehicles')}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            sx={{ 
                              bgcolor: getStatusColor(vehicle.status, vehicle.maintenance_status) === 'success' ? 'success.main' : 
                                      getStatusColor(vehicle.status, vehicle.maintenance_status) === 'warning' ? 'warning.main' : 
                                      getStatusColor(vehicle.status, vehicle.maintenance_status) === 'info' ? 'info.main' : 'primary.main',
                              width: 48,
                              height: 48
                            }}
                          >
                            <CarIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight="medium">
                              {vehicle.marque} {vehicle.modele}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {vehicle.registration_number}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • {vehicle.year}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • {vehicle.mileage.toLocaleString()} km
                              </Typography>
                            </Stack>
                          }
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <Box sx={{ ml: 2 }}>
                          <Chip
                            label={getStatusLabel(vehicle.status, vehicle.maintenance_status)}
                            color={getStatusColor(vehicle.status, vehicle.maintenance_status)}
                            size="small"
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Box>
                      </ListItem>
                      {index < 4 && <Divider sx={{ opacity: 0.3 }} />}
                    </React.Fragment>
                  ))}
                </List>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ 
            height: 400, 
            borderRadius: 2, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  Alertes Maintenance
                </Typography>
                {getMaintenanceAlertVehicleCount() > 0 && (
                  <Badge badgeContent={getMaintenanceAlertVehicleCount()} color="error">
                    <WarningIcon color="warning" />
                  </Badge>
                )}
              </Stack>
              {getMaintenanceAlertVehicleCount() > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Aperçu des 3 premiers véhicules concernés.
                </Typography>
              )}
              
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {getMaintenanceAlertVehicleCount() === 0 ? (
                  <Box textAlign="center" py={4}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Aucune alerte
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tous les véhicules sont en bon état
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                  {getMaintenanceAlerts().slice(0, 3).map((vehicle, index) => (
                    <React.Fragment key={vehicle.id}>
                      <ListItem 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          mb: 1,
                          bgcolor: 'warning.50',
                          border: '1px solid',
                          borderColor: 'warning.200',
                          '&:hover': { 
                            bgcolor: 'warning.100',
                            cursor: 'pointer'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                        onClick={() => navigate('/maintenance')}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                            <ScheduleIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight="medium">
                              {vehicle.marque} {vehicle.modele}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {vehicle.registration_number}
                            </Typography>
                          }
                        />
                        <IconButton size="small" color="warning" aria-label="Voir la maintenance de ce véhicule">
                          <VisibilityIcon />
                        </IconButton>
                      </ListItem>
                      {index < 2 && <Divider sx={{ opacity: 0.3 }} />}
                    </React.Fragment>
                  ))}
                </List>
                )}
                
                {getMaintenanceAlertVehicleCount() > 3 && (
                  <Box textAlign="center" mt={2}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate('/maintenance')}
                      sx={{ borderRadius: 2 }}
                    >
                      Voir toutes les alertes ({getMaintenanceAlertVehicleCount()} véhicules)
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Low Stock Items Section */}
      {lowStockItems.length > 0 && (
        <Card sx={{ mt: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Stock Faible - Équipements
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {lowStockItems.length === 1
                    ? '1 équipement nécessite un réapprovisionnement'
                    : `${lowStockItems.length} équipements nécessitent un réapprovisionnement`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<InventoryIcon />}
                onClick={() => navigate('/equipment')}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
                  }
                }}
              >
                Gérer les équipements
              </Button>
            </Stack>
            
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  fontSize: 24
                }
              }}
            >
              <Typography variant="subtitle2" fontWeight="medium">
                Attention requise
              </Typography>
              <Typography variant="body2">
                Ces équipements ont un niveau de stock faible et nécessitent un réapprovisionnement urgent.
              </Typography>
            </Alert>
            
            <List sx={{ p: 0 }}>
              {lowStockItems.slice(0, 5).map((item, index) => {
                const shortage = item.minimum_stock_level - item.quantity_available;
                const stockPercentage = (item.quantity_available / item.minimum_stock_level) * 100;
                return (
                  <React.Fragment key={item.id}>
                    <ListItem 
                      sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        mb: 2,
                        bgcolor: item.quantity_available === 0 ? 'error.50' : 'warning.50',
                        border: '1px solid',
                        borderColor: item.quantity_available === 0 ? 'error.200' : 'warning.200',
                        '&:hover': { 
                          bgcolor: item.quantity_available === 0 ? 'error.100' : 'warning.100',
                          cursor: 'pointer'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => navigate('/equipment')}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: item.quantity_available === 0 ? 'error.main' : 'warning.main',
                            width: 48,
                            height: 48
                          }}
                        >
                          <WarningIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6" fontWeight="medium">
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Stock actuel: <strong>{item.quantity_available}</strong>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • Minimum: <strong>{item.minimum_stock_level}</strong>
                              </Typography>
                              {item.category && (
                                <Typography variant="body2" color="text.secondary">
                                  • {item.category}
                                </Typography>
                              )}
                            </Stack>
                            <Box sx={{ mb: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(stockPercentage, 100)} 
                                color={item.quantity_available === 0 ? 'error' : 'warning'}
                                sx={{ 
                                  height: 6, 
                                  borderRadius: 3,
                                  bgcolor: 'grey.200'
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="error.main" fontWeight="medium">
                              Manque:{' '}
                              {shortage <= 0
                                ? 'aucune unité'
                                : shortage === 1
                                  ? '1 unité'
                                  : `${shortage} unités`}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Chip
                          label={item.quantity_available === 0 ? 'Rupture' : 'Faible'}
                          color={item.quantity_available === 0 ? 'error' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Box>
                    </ListItem>
                    {index < 4 && <Divider sx={{ opacity: 0.3 }} />}
                  </React.Fragment>
                );
              })}
            </List>
            
            {lowStockItems.length > 5 && (
              <Box textAlign="center" mt={3}>
                <Button 
                  variant="outlined" 
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/equipment')}
                  sx={{ borderRadius: 2 }}
                >
                  Voir tous les équipements en stock faible ({lowStockItems.length})
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      </Container>
    </Box>
  );
};

export default ClientDashboard;
